<template>
  <div v-if="i18nReady" class="login-page d-flex min-vh-100">
    <!-- LEWA KOLUMNA: baner -->
    <aside class="left-pane d-none d-lg-flex flex-column justify-content-between p-5">
      <header class="d-flex align-items-center">
        <h1 class="brand mb-0">{{ $t('login.brand') }}</h1>
      </header>

      <div class="promo">
        <p class="eyebrow">{{ $t('login.promo.eyebrow') }}</p>
        <h2 class="headline text-white">{{ $t('login.promo.headline') }}</h2>

        <ul class="news mt-4" aria-live="polite">
          <li v-for="(item, idx) in visibleNews" :key="idx">
            <span class="dot" aria-hidden="true"></span>{{ item }}
          </li>
        </ul>
      </div>

      <footer class="small text-white-50">
        © {{ new Date().getFullYear() }} {{ $t('login.brand') }}. B2B only.
      </footer>
    </aside>

    <!-- PRAWA KOLUMNA: logowanie -->
    <main class="right-pane d-flex align-items-center justify-content-center w-100">
      <div class="card shadow p-4 p-md-5 form-card bg-white">
        <div class="d-flex justify-content-between align-items-start mb-4">
          <h2 class="fw-bold mb-0">{{ $t('login.auth.loginTitle') }}</h2>

          <!-- Selektor języka -->
          <div class="lang-select">
            <label class="form-label m-0 me-2 visually-hidden" for="lang">
              {{ $t('login.auth.languageLabel') }}
            </label>
            <select id="lang" name="lang" class="form-select" v-model="language"
              :aria-label="$t('login.auth.languageAria')" autocomplete="language" @change="onLanguageChange">
              <option v-for="opt in langOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <form @submit.prevent="login" novalidate>
          <div class="mb-3">
            <label for="email" class="form-label">{{ $t('login.auth.email') }}</label>
            <input id="email" name="email" v-model.trim="email" type="email" class="form-control" inputmode="email"
              autocomplete="username email" required :placeholder="$t('login.auth.emailPlaceholder')" />
          </div>

          <div class="mb-3">
            <label for="password" class="form-label">{{ $t('login.auth.password') }}</label>
            <input id="password" name="password" v-model="password" type="password" class="form-control"
              autocomplete="current-password" required :placeholder="$t('login.auth.passwordPlaceholder')" />
          </div>

          <div class="d-flex justify-content-between align-items-center mb-4">
            <!-- <a href="#" @click.prevent class="link-secondary">
              {{ $t('login.auth.forgot') }}
            </a> -->
          </div>

          <button :disabled="loading" type="submit" id="login-button" name="login-button"
            class="btn btn-brand w-100 py-2 text-white">
            <span v-if="!loading">{{ $t('login.auth.login') }}</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-if="loading" class="ms-2">...</span>
          </button>

          <p v-if="error" class="text-danger small mt-3" role="alert">{{ error }}</p>
        </form>

        <p class="small text-muted mt-4 mb-0">
          {{ $t('login.legal.foot') }}
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import { applyLocaleForView, fetchLocaleOptions, toBCP47 } from '../i18n/index.js'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'LoginView',
  data() {
    return {
      i18nReady: false,
      email: '',
      password: '',
      remember: false,
      loading: false,
      error: '',
      language: 'pl-PL',
      langOptions: [],
      newsIndex: 0,
      rotator: null
    }
  },
  computed: {
    newsList() {
      const arr = this.$tm ? this.$tm('login.promo.news') : []
      return Array.isArray(arr) ? arr : []
    },
    visibleNews() {
      const n = this.newsList.length
      if (!n) return []
      const i = this.newsIndex % n
      const next = (i + 1) % n
      return [this.newsList[i], this.newsList[next]]
    }
  },
  async mounted() {
    // 1) przywróć język
    const savedLang = localStorage.getItem('sv-lang') || this.language
    this.language = await applyLocaleForView('LoginView', savedLang)
    this.i18nReady = true

    // 2) ZA KAŻDYM WEJŚCIEM odśwież listę języków (jednorazowo na mount)
    this.langOptions = await fetchLocaleOptions(true)

    // jeżeli aktualny nie istnieje na liście, przestaw na pierwszy i doładuj
    if (!this.langOptions.find(o => o.value === this.language)) {
      const first = this.langOptions[0]?.value || 'pl-PL'
      this.language = await applyLocaleForView('LoginView', first)
    }

    // 3) rotator newsów
    this.rotator = setInterval(() => {
      if (this.newsList.length) {
        this.newsIndex = (this.newsIndex + 1) % this.newsList.length
      }
    }, 3500)
  },
  beforeUnmount() {
    clearInterval(this.rotator)
  },
  methods: {
    async onLanguageChange(evt) {
      const val = evt?.target?.value ?? this.language
      this.language = toBCP47(val)
      await applyLocaleForView('LoginView', this.language)
    },

    async login() {
      this.loading = true
      this.error = ''
      try {
        // CSRF: ustaw cookie XSRF-TOKEN przed pierwszym POST
        await axios.get('/api/auth/csrf')

        const res = await axios.post(
          '/api/auth/login',
          {
            email: this.email,
            password: this.password,
            lang: this.language
          },
          { headers: { 'Accept-Language': this.language } }
        )

        const { email, roles } = res.data || {}

        // Session-cookie: profil trzymamy w Pinia (bez tokenów w localStorage)
        const auth = useAuthStore()
        auth.setProfile(res.data)

        localStorage.setItem('sv-lang', this.language)


        if (this.remember) localStorage.setItem('remember-email-B2B', this.email)
        else localStorage.removeItem('remember-email-B2B')

        const roleNames = (Array.isArray(roles) ? roles : [])
          .map(r => (typeof r === 'string' ? r : r?.name))
          .filter(Boolean)
          .map(r => r.toUpperCase())

        const isEmployee = roleNames.includes('PRACOWNIK')
        this.$router.push(isEmployee ? '/shop' : '/shop')
      } catch (e) {
        console.error(e)
        this.error = this.$t('login.auth.error')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
:root {
  --brand: #044a40;
  --grid: rgba(255, 255, 255, .06);
}

.login-page {
  background: #f6f7f9;
}

/* LEWA KOLUMNA – zielone tło marki */
.left-pane {
  flex: 0 0 50%;
  color: #fff;
  background: linear-gradient(140deg, #066457 0%, #044a40 45%, #033830 100%);
  position: relative;
  overflow: hidden;
}

.left-pane::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(transparent 95%, var(--grid) 95%),
    linear-gradient(90deg, transparent 95%, var(--grid) 95%);
  background-size: 44px 44px;
  opacity: .35;
  pointer-events: none;
}

.brand {
  font-weight: 800;
  letter-spacing: .5px;
  color: #fff;
}

.eyebrow {
  color: #e7f4f2;
  text-transform: uppercase;
  letter-spacing: .16em;
  font-weight: 700;
  font-size: .8rem;
  margin-bottom: .25rem;
}

.headline {
  font-weight: 800;
  line-height: 1.2;
  max-width: 28rem;
}

.news {
  list-style: none;
  padding: 0;
  margin: 0;
}

.news li {
  display: flex;
  align-items: center;
  gap: .6rem;
  padding: .5rem 0;
  color: #f2fbf9;
}

.news .dot {
  display: inline-block;
  width: .5rem;
  height: .5rem;
  border-radius: 50%;
  background: #ffffff;
  opacity: .8;
}

/* PRAWA KOLUMNA */
.right-pane {
  flex: 1;
  padding: 2rem;
}

.form-card {
  max-width: 500px;
  width: 100%;
}

.btn-brand {
  background-color: #044a40;
  border-color: #044a40;
}

.btn-brand:hover {
  filter: brightness(0.95);
}

.visually-hidden {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .right-pane {
    padding: 1.25rem;
  }
}
</style>
